import { NgClass, NgStyle } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  forwardRef,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import PrimeOverlayComponent from '@iupics-components/overrided/prime-overlay/prime-overlay.component';
import FieldConflictPanelComponent from '../field-conflict-panel/field-conflict-panel.component';
import { AbstractDataContainer } from '@iupics-manager/models/abstract-datacontainer';
import { IupicsDataField } from '@iupics-manager/models/iupics-data';
import { OverlayPanel } from 'primeng/overlaypanel';
import { TooltipModule } from 'primeng/tooltip';

import { ApizJsonField } from '@compiere-ws/models/apiz-domains';
import { MessageManagerService } from '@iupics-manager/managers/message/message-manager.service';
import { IupicsMessage } from '@iupics-manager/models/iupics-message';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { Nullable } from 'primeng/ts-helpers';
import EditTabUiComponent from '../../layouts/edit-tab-ui/edit-tab-ui.component';
import ValuePreferencePanelComponent from '../../value-preference-panel/value-preference-panel.component';
import { LabelUiComponent } from '../label-ui/label-ui.component';
import InputJsonLineUiComponent from './input-json-line-ui/input-json-line-ui.component';

@Component({
  selector: 'iu-input-json-ui',
  templateUrl: './input-json-ui.component.html',
  styleUrls: ['./input-json-ui.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    TooltipModule,
    NgClass,
    FormsModule,
    PrimeOverlayComponent,
    FieldConflictPanelComponent,
    forwardRef(() => ValuePreferencePanelComponent),
    LabelUiComponent,
    ButtonModule,
    NgStyle,
    TranslateModule,
  ],
})
export default class InputJsonUiComponent extends AbstractDataContainer implements OnInit, AfterViewInit {
  readonly #translateService = inject(TranslateService);
  readonly #messageManager = inject(MessageManagerService);

  hasMultiValues = false;
  fields: ApizJsonField[] = [];
  focused: Nullable<boolean>;
  isNotAuthorized: Nullable<boolean>;
  @Input() addOnBlur = false;
  @Input() addOnEnter = true;
  @Input() addOnTab = true;
  @Input() addOnSpace = false;
  @Input() editOnBackspace = true;
  @Input() matchPattern: RegExp = /.*/;
  @Input() inputStyle: { [key: string]: string };
  @Input() placeholder: string;
  private globalClickListener: Function;
  onModelChange: Function;
  _chips = [];
  @Output() chipsChange = new EventEmitter<any>();
  @Output() onChipAdded = new EventEmitter<any>();
  @Output() onChipRemoved = new EventEmitter<any>();
  @ViewChild('opConflict', { static: true }) opConflict: FieldConflictPanelComponent;
  @ViewChild('opCreation', { static: true }) opCreation: OverlayPanel;
  @ViewChild('vcrCreation', { read: ViewContainerRef, static: true })
  vcrCreation: ViewContainerRef;
  @ViewChild('jsonFieldsContainer', { read: ElementRef, static: true }) jsonFieldsContainer: ElementRef<HTMLDivElement>;
  @ViewChild('inputtext', { read: ElementRef, static: true })
  inputViewChild: ElementRef<HTMLInputElement>;
  @ViewChild('jsonBody', { read: ElementRef, static: true }) jsonBody: ElementRef<HTMLDivElement>;
  @Input() data: IupicsDataField;
  @Input()
  columnName: string;

  @Input() placeHolder: string;
  fieldKeys: ApizJsonField[] = [];
  fieldDisplayeds: ApizJsonField[] = [];
  uniqueProp: boolean = false;
  currentChip;
  isFree = true;
  isFromInputJson = false;
  isSplitButtonOpen = false;
  // chip armed by a first Backspace (removed on the second one) / chip flashed on a duplicate add
  selectedChipIndex = -1;
  duplicateChipIndex = -1;
  #duplicateTimeout: ReturnType<typeof setTimeout>;
  // creation panel: draft committed by Add / Save only; the edited chip (undefined = new chip)
  #editedChip: any;
  get isEditingChip(): boolean {
    return !!this.#editedChip;
  }

  get panelTitleKeyNew(): string {
    return this.isTagField ? 'inputJson.addTagTitle' : 'inputJson.addTitle';
  }

  get panelTitleKey(): string {
    if (this.isTagField) return this.isEditingChip ? 'inputJson.editTagTitle' : 'inputJson.addTagTitle';
    return this.isEditingChip ? 'inputJson.editTitle' : 'inputJson.addTitle';
  }

  // "Tags" JSON columns get the tag look (# prefix, tag wording)
  get isTagField(): boolean {
    return /tags?$/i.test(this.data?.columnName ?? this.columnName ?? '');
  }

  getChipTitle(chip: any): string {
    if (!chip) return '';
    return (this.fieldDisplayeds ?? [])
      .map((f) =>
        this.fieldDisplayeds.length === 1 ? `${chip[f.name] ?? ''}` : `${f.nameToShow} = ${chip[f.name] ?? ''}`
      )
      .join(' | ');
  }

  // inline add: trimmed value, case-insensitive duplicate -> flash the existing chip (returns true if handled)
  #flashDuplicate(value: string): boolean {
    const key = this.fields?.[0]?.name;
    const idx = key ? (this.chips ?? []).findIndex((c) => `${c?.[key] ?? ''}`.toLowerCase() === value.toLowerCase()) : -1;
    if (idx === -1) return false;
    this.#flashChip(idx);
    return true;
  }

  #flashChip(idx: number) {
    clearTimeout(this.#duplicateTimeout);
    this.duplicateChipIndex = -1;
    setTimeout(() => (this.duplicateChipIndex = idx));
    this.#duplicateTimeout = setTimeout(() => (this.duplicateChipIndex = -1), 700);
  }

  // case-insensitive key + value signature of a chip (_ID and empty values ignored)
  #chipSignature(chip: any): string {
    return Object.keys(chip ?? {})
      .filter((k) => k !== '_ID' && this.#isFilled(chip[k]))
      .map((k) => `${k}=${JSON.stringify(chip[k])}`.toLowerCase())
      .sort()
      .join('|');
  }

  #isFilled(value: any): boolean {
    if (value === undefined || value === null) return false;
    if (value instanceof Object) return Object.keys(value).some((k) => k !== '_ID');
    return `${value}`.trim() !== '';
  }

  #panelLines(): InputJsonLineUiComponent[] {
    return (this.componentRefs ?? [])
      .map((ref) => ref?.instance)
      .filter((i) => i instanceof InputJsonLineUiComponent);
  }

  // Add / Save enabled: a name (free JSON) and a value are typed
  canCommitPanel(): boolean {
    const lines = this.#panelLines();
    if (this.isFree) {
      return lines.some((l) => this.#isFilled(l.getTypedKey()) && this.#isFilled(l.getTypedValue()));
    }
    const keyNames = this.fieldKeys.map((f) => f.name);
    if (keyNames.length === 0) return lines.some((l) => this.#isFilled(l.getTypedValue()));
    const keyLines = lines.filter((l) => keyNames.includes(l.jsonField?.name));
    return keyLines.length > 0 && keyLines.every((l) => this.#isFilled(l.getTypedValue()));
  }

  commitPanel(event?: Event) {
    event?.stopPropagation();
    for (const line of this.#panelLines()) line.flush();
    if (!this.canCommitPanel()) return;
    const draft = this.currentChip;
    const editedIdx = this.#editedChip ? (this.chips ?? []).indexOf(this.#editedChip) : -1;
    const signature = this.#chipSignature(draft);
    const dupIdx = (this.chips ?? []).findIndex((c, i) => i !== editedIdx && this.#chipSignature(c) === signature);
    if (dupIdx > -1) {
      this.#flashChip(dupIdx);
      return;
    }
    if (this.addChip(draft, this.#editedChip?._ID, this.generateId(draft))) {
      this.closePanel();
    }
  }

  closePanel(event?: Event) {
    event?.stopPropagation();
    this.isSplitButtonOpen = false;
    this.opCreation.hide();
    this.#editedChip = undefined;
  }

  // Enter in a name goes to its value, Enter in a value adds; Escape cancels
  onPanelKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.closePanel();
      return;
    }
    if (event.key !== 'Enter') return;
    const lines = this.#panelLines();
    const keyLine = lines.find((l) => l.isKeyInput(event.target));
    if (keyLine) {
      event.preventDefault();
      keyLine.focusValue();
    } else if (lines.some((l) => l.isValueInput(event.target))) {
      event.preventDefault();
      this.commitPanel();
    }
  }

  // panel under the field, right edges aligned (mobile fullscreen kept)
  alignCreationPanel = (ctx: PrimeOverlayComponent) => {
    const container = ctx?.container as HTMLElement;
    const target = ctx?.target as HTMLElement;
    if (!container || !target || container.style.width === '100%') return;
    const rect = target.getBoundingClientRect();
    const scrollLeft = window.scrollX || document.documentElement.scrollLeft || 0;
    const left = Math.max(scrollLeft, rect.right + scrollLeft - container.offsetWidth);
    container.style.left = left + 'px';
    container.style.setProperty('--overlayArrowLeft', `${Math.max(0, container.offsetWidth - 28)}px`);
  };

  @Input()
  set chips(value: any) {
    if (value instanceof Array) {
      this.generateDisplayedKeys(value);
      for (const c of value) {
        c._ID = this.generateId(c);
      }
      this.generateDisplayedKeys(value);
    }
    this._chips = value;
  }

  get chips() {
    return this._chips;
  }
  @Input()
  set fieldValue(value: any) {
    this._fieldValue = value;
    if (value != null && value != undefined) {
      if (!(value instanceof Object)) {
        let fieldValueJson = this.parseJson(value);
        if (fieldValueJson != null && fieldValueJson != undefined && !Array.isArray(fieldValueJson)) {
          fieldValueJson = [fieldValueJson];
        }
        this.chips = fieldValueJson;
      } else {
        this.chips = [value];
      }
    } else {
      this.chips = [];
    }
  }

  get fieldValue() {
    return this._fieldValue;
  }

  ngOnInit() {
    super.ngOnInit();
    if (this.cssClass !== undefined) {
      this.cssGrid = this.cssClass;
    }
    this.cssClass = ' ' + this.cssGrid;
    this.initJsonStructureData();
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    let parentComp = this.DOMParentComponent;
    while (parentComp && !(parentComp instanceof EditTabUiComponent)) {
      parentComp = parentComp.DOMParentComponent;
    }
  }

  showConflictPanel(ev) {
    this.opConflict.toggle(ev);
  }

  dataChange(value) {
    super.dataChange(value);
  }

  parseJson(value: string) {
    return JSON.parse(value);
  }

  buildPanel() {
    this.isSplitButtonOpen = false;
    this.vcrCreation.clear();
    this.componentRefs = [];
    if (!this.isFree) {
      for (const f of this.fields) {
        this.buildLine(f);
      }
    } else {
      const keys = Object.keys(this.currentChip).filter((k) => k != '_ID');
      let i = 0;
      if (keys.length == 0) {
        this.buildLine();
      }
      for (const k of keys) {
        this.buildLine({
          componentName: this.currentChip[k] instanceof Object ? 'InputJsonUiComponent' : 'InputTextUiComponent',
          description: '',
          name: k,
          nameToShow: k,
          numberType: null,
          seqNo: i++,
          jsonFieldId: i++,
          isKey: false,
          isDisplayed: false,
        });
      }
    }
  }

  initJsonStructureData() {
    if (this?.data?.jsonDefId > 0) {
      const jsonDef = this.store.getJsonDef(this?.data?.jsonDefId);
      this.hasMultiValues = jsonDef.hasMultiValues;
      this.fields = jsonDef.fields as ApizJsonField[];
      this.isFree = this.fields.length == 0;
      this.uniqueProp = this.fields.length == 1;
      this.fieldKeys = this.fields.filter((f) => f.isKey);
      if (!this.isFree || this.fields.length > 0) this.fieldDisplayeds = this.fields.filter((f) => f.isDisplayed);
    }
  }

  buildObjectLine() {
    this.buildLine({
      componentName: 'InputJsonUiComponent',
      description: '',
      name: '',
      nameToShow: '',
      numberType: null,
      seqNo: -1,
      jsonFieldId: -1,
      isKey: false,
      isDisplayed: false,
    });
  }
  buildLine(jsonField?: ApizJsonField) {
    if (!jsonField) {
      jsonField = {
        componentName: 'InputTextUiComponent',
        description: '',
        name: '',
        nameToShow: '',
        numberType: null,
        seqNo: -1,
        jsonFieldId: -1,
        isKey: false,
        isDisplayed: false,
      };
    }
    const componentRef = this.vcrCreation.createComponent(InputJsonLineUiComponent);
    componentRef.instance.keyPlaceholder = this.#translateService.instant(
      this.isTagField ? 'inputJson.tagName' : 'inputJsonLine.key'
    );
    // JsonDef field label if any
    componentRef.instance.valuePlaceholder =
      !this.isFree && jsonField.nameToShow
        ? jsonField.nameToShow
        : this.#translateService.instant(this.isTagField ? 'inputJson.tagValue' : 'inputJsonLine.value');
    componentRef.instance.dataChangeEmitter.subscribe((value) => this.lineChange(value));
    componentRef.instance.jsonField = jsonField;
    componentRef.instance.value = this.currentChip[jsonField.name];
    componentRef.instance.key = jsonField.name;
    componentRef.instance.nameToShow = jsonField.nameToShow;
    componentRef.instance.isFree = this.isFree;
    this.componentRefs.push(componentRef);
  }

  // updates the draft only (committed by commitPanel)
  lineChange(value: any) {
    const keys = Object.keys(value);
    for (const k of keys) {
      if (k === '' || value[k] === undefined || value[k] === null) {
        delete this.currentChip[k];
      } else {
        this.currentChip[k] = value[k];
      }
    }
  }

  addChip(chip: any, oldId: string, newId: string): boolean {
    this.isNotAuthorized = false;
    if (chip) {
      let foundIndex = this.chips.findIndex((t) => t._ID === oldId);
      if (newId != oldId && this.chips.find((t) => t._ID === newId)) {
        this.#messageManager.newMessage(
          new IupicsMessage(
            this.#translateService.instant('generic.error'),
            this.#translateService.instant('inputJson.duplicateKey'),
            'error'
          )
        );
        return false;
      }

      if (newId?.length > 0) {
        chip._ID = newId;
        if (foundIndex == -1) {
          this.chips = [...this.chips, chip];
        } else {
          this.chips = [
            ...this.chips.slice(0, foundIndex), // Elements before foundIndex
            chip, // New chip
            ...this.chips.slice(foundIndex + 1), // Elements after foundIndex
          ];
        }
      } else if (foundIndex > -1) {
        this.chips = [...this.chips.slice(0, foundIndex), ...this.chips.slice(foundIndex + 1)];
      } else return false;
    }
    this.updateModel();
    this.chipsChange.emit(this.chips);
    this.onChipAdded.emit();
    return true;
  }

  removeJsonChip(event, index: number): void {
    if (event) {
      event.stopPropagation();
    }
    if (!this.isReadOnly) {
      this.chips.splice(index, 1);
      this.updateModel();
      this.chipsChange.emit(this.chips);
      this.onChipRemoved.emit();
    }
  }

  removeChipById(id: string): void {
    const idx = this.chips.findIndex((c) => c._ID === id);
    if (idx > -1) {
      this.removeJsonChip(null, idx);
    }
  }

  showCreationPanel(event, chip?: any) {
    if (event) {
      event.stopPropagation();
    }
    if (!this.uniqueProp && !this.isReadOnly) {
      // edit a copy: the chip changes only on Save
      this.#editedChip = chip;
      this.currentChip = chip ? { ...chip } : {};
      this.buildPanel();
      this.opCreation.show(event, this.jsonBody?.nativeElement);
      setTimeout(() => this.#panelLines()[0]?.focusFirst(), 200);
    }
  }

  updateModel() {
    let value;
    if (!this.hasMultiValues) {
      value = this.chips[0];
    } else {
      value = this.chips;
    }
    if (value === undefined) {
      value = null;
    }
    if (value != null) {
      if (!Array.isArray(value)) {
        value = { ...value, _ID: undefined };
      }
      if (!this.isFromInputJson) value = JSON.stringify(value);
    }
    this.dataChange(value);
  }

  addLineToPanel(event: Event, isObject = false) {
    if (event?.stopPropagation) {
      event.stopPropagation();
    }
    if (this.isFree) {
      if (isObject) {
        this.buildObjectLine();
      } else {
        this.buildLine();
      }
      if (this.jsonFieldsContainer) {
        setTimeout(() => {
          this.gotoBottom();
          const lines = this.#panelLines();
          lines[lines.length - 1]?.focusFirst();
        }, 50);
      }
    }
  }

  private gotoBottom() {
    if (this.jsonFieldsContainer?.nativeElement) {
      this.jsonFieldsContainer.nativeElement.scrollTop =
        this.jsonFieldsContainer.nativeElement.scrollHeight - this.jsonFieldsContainer.nativeElement.clientHeight;
    }
  }

  onInputFocus(event: FocusEvent) {
    if (this.globalClickListener === undefined) {
      this.globalClickListener = this.renderer.listen(document, 'click', (e: any) => {
        const path = e.path ? e.path : e.composedPath();
        if (path.indexOf(this.elementRef?.nativeElement) < 0) {
          this.globalClickListener();
          this.globalClickListener = undefined;
        }
      });
    }
  }

  onInputBlur(event) {
    this.focused = false;
    if (this.addOnBlur && this.uniqueProp && this.inputViewChild?.nativeElement.value) {
      const values: any = {};
      values[this.fields[0].name] = this.inputViewChild?.nativeElement.value;
      this.addChip(values, null, this.inputViewChild?.nativeElement.value);
      if (this.inputViewChild?.nativeElement.value) {
        this.inputViewChild.nativeElement.value = '';
      }
    }
  }

  // inline add (single property JSON): trimmed value, no case-insensitive duplicate
  #addInlineValue(): boolean {
    const input = this.inputViewChild?.nativeElement;
    const value = (input?.value ?? '').trim();
    if (value === '') {
      if (input) input.value = '';
      return false;
    }
    if (this.#flashDuplicate(value)) {
      return false;
    }
    const values: any = {};
    values[this.fields[0].name] = value;
    if (!this.addChip(values, null, value)) {
      this.isNotAuthorized = true;
      return false;
    }
    if (input) input.value = '';
    return true;
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Backspace') {
      this.selectedChipIndex = -1;
    }
    switch (event.key) {
      // Backspace: the first one selects the last chip, the second one removes it
      case 'Backspace':
        if (this.inputViewChild?.nativeElement.value.length === 0 && this.chips && this.chips.length > 0) {
          if (this.selectedChipIndex !== this.chips.length - 1) {
            this.selectedChipIndex = this.chips.length - 1;
            event.preventDefault();
            break;
          }
          this.selectedChipIndex = -1;
          this.chips = [...this.chips];
          const removedItem = this.chips.pop();
          this.updateModel();
          this.chipsChange.emit(this.chips);
          if (this.editOnBackspace) {
            if (this.inputViewChild?.nativeElement) {
              this.inputViewChild.nativeElement.value = removedItem[this.fields[0].name];
            }
            event.preventDefault();
          }
        }
        break;

      // Enter
      case 'Enter':
        if (this.addOnEnter && this.inputViewChild?.nativeElement.value !== '') {
          if (!this.#addInlineValue()) {
            break;
          }
          event.preventDefault();
          this.inputViewChild?.nativeElement.blur();
          this.onInputBlur(event);
        }
        break;

      // Tab
      case 'Tab':
        if (this.addOnTab && this.inputViewChild?.nativeElement.value !== '') {
          if (!this.#addInlineValue()) {
            break;
          }
          event.preventDefault();
        }
        break;

      // Space
      case ' ':
        if (this.addOnSpace && this.inputViewChild?.nativeElement.value !== '') {
          if (!this.#addInlineValue()) {
            break;
          }
          event.preventDefault();
        }
        break;

      default:
        break;
    }
  }
  aggregateValuesWithDelimiter(obj: any, delimiter: string = '|', keys?: string[]): string {
    const result: string[] = [];
    this.traverseObject(obj, result, keys);
    return result ? result.join(delimiter) : '';
  }

  traverseObject(obj: any, result: string[], keysOverride?: string[]): void {
    if (result == null) {
      return;
    }
    if (obj !== null && typeof obj === 'object') {
      if (Array.isArray(obj)) {
        for (const item of obj) {
          this.traverseObject(item, result);
        }
      } else {
        const keys = keysOverride
          ? keysOverride
          : Object.keys(obj)
              .sort()
              .filter((k) => k != '_ID');
        for (const key of keys) {
          this.traverseObject(obj[key], result);
        }
      }
    } else if (obj === null || obj === undefined || obj.length == 0) {
      result = null;
    } else {
      result.push(String(obj));
    }
  }
  generateId(chip: any): string {
    let _ID = '';
    const keys = this.fieldKeys.filter((fk) => fk.name != '_ID');
    if (this.isFree && keys.length === 0) {
      _ID = this.aggregateValuesWithDelimiter(chip, '|');
    } else {
      _ID = this.aggregateValuesWithDelimiter(
        chip,
        '|',
        keys.map((f) => f.name)
      );
    }
    return _ID;
  }
  generateDisplayedKeys(chips: any) {
    if (this.isFree) {
      this.fieldDisplayeds = [];
      let i = 0;
      for (const c of chips) {
        const keys = Object.keys(c);
        for (const k of keys) {
          if (k != '_ID' && !(c[k] instanceof Object) && this.fieldDisplayeds.findIndex((fd) => fd.name === k) === -1) {
            this.fieldDisplayeds.push({
              componentName: 'InputTextUiComponent',
              description: k,
              name: k,
              nameToShow: k,
              numberType: null,
              seqNo: i++,
              jsonFieldId: i++,
              isKey: false,
              isDisplayed: true,
            });
          }
        }
      }
    }
  }
}
